import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import AppWideMetrics from '@gumtree/web-bff/src/metrics/modules/app-wide-metrics';
import { getHasUserMadeChoice } from './util';
import { setConsentStatusParams, waitForConsentUpdate } from './track-consent-state';

const trackingEventsOnOneTrust = async (window) => {
    await getHasUserMadeChoice(window); // wait for banner load

    const preferenceOptionsLookup: Record<string, GA4.PrivacyPrefKey> = {
        'Functional Cookies': 'functionalCookie',
        'Performance Cookies': 'performanceCookie',
        'Targeting Cookies': 'targetingCookie',
        'Google Advertising': 'googleAdvertising',
        'Adobe Marketplace': 'adobeMarketplace',
        'Microsoft Clarity': 'microsoftClarity',
        TikTok: 'tiktok',
        'TV Squared': 'tvSquared',
        'Google ATPs': 'googleAtps',
        Qualaroo: 'qualaroo',
        'Facebook Advertising': 'facebookAdvertising',
        LiveRamp: 'liveramp',
        'Store and/or access information on a device': 'storeAccessDeviceData',
        'Personalised advertising and content, advertising and content measurement, audience research and services development':
            'personalisationData',
        'Use precise geolocation data': 'geolocationData',
        'Actively scan device characteristics for identification': 'deviceIdentificationData',
    };

    const initiatePreferenceOptions = () => {
        const preferenceOptions = {} as Partial<Record<GA4.PrivacyPrefKey, boolean>>;
        const toggles = window.document.querySelectorAll('.ot-tgl input[type="checkbox"]');

        toggles.forEach((toggle) => {
            const label = toggle.closest('.ot-tgl')?.querySelector('.ot-label-txt');
            const labelValue = label?.textContent.trim();

            if (labelValue && preferenceOptionsLookup[labelValue]) {
                preferenceOptions[preferenceOptionsLookup[labelValue]] = toggle.checked;
            }
        });

        return preferenceOptions;
    };

    const resetPrivacyPrefObj = () => {
        trackGA4Event<GA4.UpdatePrivacyPreferencesEvent>({
            event: 'update_privacy_preferences',
            privacyPref: undefined,
            consentStatus: setConsentStatusParams(),
        });
    };

    window.document.body.addEventListener('click', async (event) => {
        const oneTrustUI = window.document.querySelector('#onetrust-consent-sdk');
        const isClickInsideOneTrustUI = oneTrustUI && oneTrustUI.contains(event?.target);
        const defaultPreferenceOptions = initiatePreferenceOptions();

        if (isClickInsideOneTrustUI) {
            const bannerAcceptBtn = document.querySelector('#onetrust-accept-btn-handler');
            const bannerRejectBtn = document.querySelector('#onetrust-reject-all-handler');
            const bannerManageBtn = document.querySelector('#onetrust-pc-btn-handler');
            const preferenceAcceptBtn = document.querySelector('#accept-recommended-btn-handler');
            const preferenceRejectBtn = document.querySelector('.ot-pc-refuse-all-handler');
            const preferenceSaveBtn = document.querySelector('.save-preference-btn-handler');
            const toggledNob = event.target.closest('.ot-switch-nob');

            if (bannerAcceptBtn?.contains(event?.target)) {
                waitForConsentUpdate().then(() => {
                    trackGA4Event<GA4.ConsentBannerEvent>({
                        event: 'consent_banner',
                        clickConsentBanner: 'accept',
                        consentStatus: setConsentStatusParams(),
                    });
                });

                AppWideMetrics.registerConsentBannerInteractionEvent('accept');
            }

            if (bannerRejectBtn?.contains(event?.target)) {
                waitForConsentUpdate().then(() => {
                    trackGA4Event<GA4.ConsentBannerEvent>({
                        event: 'consent_banner',
                        clickConsentBanner: 'reject',
                        consentStatus: setConsentStatusParams(),
                    });
                });

                AppWideMetrics.registerConsentBannerInteractionEvent('reject');
            }

            if (bannerManageBtn?.contains(event?.target)) {
                // For "manage options", capture current state immediately (no consent change yet)
                trackGA4Event<GA4.ConsentBannerEvent>({
                    event: 'consent_banner',
                    clickConsentBanner: 'manage options',
                    consentStatus: setConsentStatusParams(),
                });

                AppWideMetrics.registerConsentBannerInteractionEvent('manage-options');
            }

            if (preferenceAcceptBtn?.contains(event?.target)) {
                resetPrivacyPrefObj();
                waitForConsentUpdate().then(() => {
                    trackGA4Event<GA4.UpdatePrivacyPreferencesEvent>({
                        event: 'update_privacy_preferences',
                        privacyPref: { acceptAll: true },
                        consentStatus: setConsentStatusParams(),
                    });
                });

                AppWideMetrics.registerConsentBannerInteractionEvent(
                    'privacy-preferences-accept-all'
                );
            }

            if (preferenceRejectBtn?.contains(event?.target)) {
                resetPrivacyPrefObj();
                waitForConsentUpdate().then(() => {
                    trackGA4Event<GA4.UpdatePrivacyPreferencesEvent>({
                        event: 'update_privacy_preferences',
                        privacyPref: { rejectAll: true },
                        consentStatus: setConsentStatusParams(),
                    });
                });

                AppWideMetrics.registerConsentBannerInteractionEvent(
                    'privacy-preferences-reject-all'
                );
            }

            if (toggledNob) {
                const label = toggledNob.nextElementSibling;
                const input = label?.parentElement.previousElementSibling;
                const isToggledOn = !input.checked;

                if (label && label.classList.contains('ot-label-txt')) {
                    const labelValue = label.textContent.trim();
                    defaultPreferenceOptions[preferenceOptionsLookup[labelValue]] = isToggledOn;
                }
            }

            if (preferenceSaveBtn?.contains(event?.target)) {
                resetPrivacyPrefObj();
                const isRejectedAll = Object.values(defaultPreferenceOptions).every(
                    (value) => value === false
                );

                waitForConsentUpdate().then(() => {
                    trackGA4Event<GA4.UpdatePrivacyPreferencesEvent>({
                        event: 'update_privacy_preferences',
                        privacyPref: isRejectedAll
                            ? { rejectAll: true }
                            : { ...defaultPreferenceOptions },
                        consentStatus: setConsentStatusParams(),
                    });
                });
            }
        }
    });
};

export default (window) => {
    trackingEventsOnOneTrust(window);
};
